import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, TextField, List, ListItem, ListItemText,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getRooms, createRoom, deleteRoom } from "../api";

export default function ManageRooms() {
  const [rooms, setRooms] = useState([]);
  const [newRoomName, setNewRoomName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, room: null });

  const loadRooms = async () => {
    setLoading(true);
    try {
      const data = await getRooms();
      setRooms(data.rooms || ["default"]);
    } catch (err) {
      setError("Failed to load rooms");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
  }, []);

  const handleCreate = async () => {
    if (!newRoomName.trim()) return;
    setError("");
    try {
      await createRoom(newRoomName.trim());
      setNewRoomName("");
      loadRooms();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create room");
    }
  };

  const handleDeleteClick = (room) => {
    setDeleteDialog({ open: true, room });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.room) return;
    try {
      await deleteRoom(deleteDialog.room);
      loadRooms();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete room");
    } finally {
      setDeleteDialog({ open: false, room: null });
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>Manage Rooms</Typography>

      {/* Create new room */}
      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <TextField
          fullWidth
          label="New room name"
          value={newRoomName}
          onChange={(e) => setNewRoomName(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleCreate()}
        />
        <Button variant="contained" onClick={handleCreate}>
          Create
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="h6" gutterBottom>Existing Rooms</Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <List>
          {rooms.map((room) => (
            <ListItem
              key={room}
              secondaryAction={
                room !== "default" && (
                  <IconButton edge="end" onClick={() => handleDeleteClick(room)}>
                    <DeleteIcon />
                  </IconButton>
                )
              }
            >
              <ListItemText primary={room} />
            </ListItem>
          ))}
        </List>
      )}

      {/* Delete confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, room: null })}>
        <DialogTitle>Delete Room?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete room <strong>{deleteDialog.room}</strong>?<br />
          All songs in this room will be lost.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, room: null })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}